/**
 * Hook: usePredictedSuccessRate
 *
 * Combines patient data from existing hooks with the IVF prediction engine
 * to produce a personalized success rate estimate for a given clinic.
 */

import { useMemo } from "react";
import { usePatient } from "@louise/patients/hooks/usePatient";
import { useCycles } from "@src/packages/cycles/hooks/useCycles";
import { useFertilityIntent } from "@louise/fertility-intent/hooks/useFertilityIntent";
import {
  projectFertilityPrediction,
  CLINIC_PROJECTION_EVIDENCE,
  type ClinicEmbryoSource,
  type ClinicOutcomeMetric,
  type ClinicRateObservation,
  type ClinicRateTable,
  type ClinicSingleRate,
  type ClinicTreatmentType,
  type ClinicTreatmentSuccessInput,
  type ObservedValue,
} from "@louise/fertility-projections";
import type { Clinic } from "../clinics.types";
import {
  predictSuccessRate,
  type PredictionInput,
  type PredictionResult,
} from "./ivfPredictionEngine";
import { mapPatientProfile } from "./patientProfileMapper";

type UsePredictedSuccessRateOptions = {
  /** Explicitly set if patient is using donor eggs */
  isUsingDonorEggs?: boolean;
  /** Override age when patient DOB is not available */
  ageOverride?: number;
  /** Enables the pending canonical adapter for internal review only. */
  useCanonicalPrediction?: boolean;
  /** Treatment context for the canonical adapter; IVF is the legacy default. */
  treatmentType?: ClinicTreatmentType;
  embryoSource?: ClinicEmbryoSource;
};

export function usePredictedSuccessRate(
  clinic: Clinic,
  options?: UsePredictedSuccessRateOptions,
): PredictionResult | null {
  const { data: patient } = usePatient();
  const { data: fertilityIntent } = useFertilityIntent();
  const { data: cycles = [] } = useCycles();

  return useMemo(() => {
    let input: PredictionInput | null = null;

    if (patient) {
      input = mapPatientProfile(patient, fertilityIntent ?? null, cycles, {
        isUsingDonorEggs: options?.isUsingDonorEggs,
      });
    }

    // If patient profile mapping failed (e.g. no DOB) but we have an age
    // override, build a minimal input with sensible defaults
    if (!input && options?.ageOverride) {
      input = {
        age: options.ageOverride,
        diagnoses: [],
        priorFailedIvfCycles: 0,
        isUsingDonorEggs: options?.isUsingDonorEggs ?? false,
        bmi: null,
        isSmoking: false,
        isFormerSmoker: false,
        hasPriorLiveBirth: false,
        hasPriorMiscarriage: false,
      };
    }

    if (!input) return null;

    const legacyResult = predictSuccessRate(clinic, input);
    if (!options?.useCanonicalPrediction) return legacyResult;

    const result = projectFertilityPrediction({
      kind: "clinic_treatment_success",
      patient: {
        ageAtTransfer: observed(input.age, "patient-age"),
      },
      clinic: buildClinicProjectionInput(clinic, input, options),
      goal: { kind: "probability_at_least_one_live_birth" },
      evidence: clinicProjectionEvidence(clinic),
    });

    // The shared adapter intentionally blocks pending clinical review. Keep
    // the old UI contract, but never turn a pending model into patient-facing
    // personalized copy through this compatibility hook.
    if (
      result.presentation !== "patient_safe" ||
      !result.clinicDetails ||
      !result.probabilityAtLeastOneLiveBirth
    ) {
      return legacyResult;
    }

    const estimate = result.probabilityAtLeastOneLiveBirth;
    return {
      estimatedRate: Math.round(estimate.mean * 1000) / 10,
      confidenceLow: Math.round(estimate.low * 100),
      confidenceHigh: Math.round(estimate.high * 100),
      baseRate: result.clinicDetails.baseRate.mean,
      baseRateLabel: `Based on this clinic's ${Math.round(result.clinicDetails.baseRate.mean)}% rate for ${result.clinicDetails.baseRateLabel}`,
      adjustments: result.clinicDetails.adjustments.map(
        ({ factor, direction, oddsRatio }) => ({
          factor,
          direction,
          oddsRatio,
        }),
      ),
      dataQuality: result.clinicDetails.dataQuality,
    };
  }, [patient, fertilityIntent, cycles, clinic, options]);
}

function observed<T>(value: T, sourceId: string): ObservedValue<T> {
  return {
    value,
    provenance: { sourceType: "patient_reported", sourceId },
  };
}

function clinicObserved<T>(value: T, sourceId: string): ObservedValue<T> {
  return {
    value,
    provenance: { sourceType: "clinic_record", sourceId },
  };
}

function mapRateTable(
  rates: Clinic["successRates"] | undefined,
  denominators: Clinic["successRateDenominators"] | undefined,
  sourceId: string,
  context: Pick<
    ClinicRateTable,
    "treatmentType" | "population" | "embryoSource" | "metric"
  >,
): ClinicRateTable | undefined {
  if (!rates) return undefined;
  const mapped: ClinicRateTable["rates"] = {};
  (
    [
      "under35",
      "between35And37",
      "between38And40",
      "between41And42",
      "over42",
    ] as const
  ).forEach((band) => {
    const value = rates[band];
    if (typeof value === "number" && Number.isFinite(value)) {
      const observation: ClinicRateObservation = clinicObserved(
        value,
        sourceId,
      );
      const denominator = denominators?.[band];
      if (
        typeof denominator === "number" &&
        Number.isInteger(denominator) &&
        denominator > 0
      ) {
        observation.denominator = clinicObserved(
          denominator,
          `${sourceId}-${band}-denominator`,
        );
      }
      mapped[band] = observation;
    }
  });
  return Object.keys(mapped).length > 0
    ? { ...context, rates: mapped }
    : undefined;
}

function clinicSingleRate(
  value: number | null | undefined,
  denominator: number | null | undefined,
  sourceId: string,
  context: Omit<ClinicSingleRate, "rate">,
): ClinicSingleRate | undefined {
  if (typeof value !== "number" || !Number.isFinite(value)) return undefined;
  const rate: ClinicRateObservation = clinicObserved(value, sourceId);
  if (
    typeof denominator === "number" &&
    Number.isInteger(denominator) &&
    denominator > 0
  ) {
    rate.denominator = clinicObserved(denominator, `${sourceId}-denominator`);
  }
  return { ...context, rate };
}

function buildClinicProjectionInput(
  clinic: Clinic,
  input: PredictionInput,
  options: UsePredictedSuccessRateOptions,
): ClinicTreatmentSuccessInput {
  const sourcePrefix = clinicOutcomeSourcePrefix(clinic);
  const treatmentType = options.treatmentType ?? "ivf";
  const embryoSource = options.embryoSource ?? "fresh_from_fresh_eggs";
  const population = input.isUsingDonorEggs ? "donor_eggs" : "own_eggs";
  const outcomeMetric: ClinicOutcomeMetric =
    clinic.outcomeMetric ?? "live_birth_per_transfer";
  return {
    treatmentType,
    population,
    embryoSource,
    outcomeMetric,
    ownEggRates: mapRateTable(
      clinic.successRates,
      clinic.successRateDenominators,
      `${sourcePrefix}-own-egg-rates`,
      {
        treatmentType,
        population: "own_eggs",
        embryoSource,
        metric: outcomeMetric,
      },
    ),
    firstCycleOwnEggRates: mapRateTable(
      clinic.newPatientSuccessRates,
      clinic.newPatientSuccessRateDenominators,
      `${sourcePrefix}-first-cycle-rates`,
      {
        treatmentType,
        population: "own_eggs",
        embryoSource,
        metric: "live_birth_per_retrieval",
      },
    ),
    donorRates: clinic.donorSuccessRates
      ? {
          freshEmbryosFreshEggs: clinicSingleRate(
            clinic.donorSuccessRates.freshEmbryosFreshEggs,
            clinic.donorSuccessRateDenominators?.freshEmbryosFreshEggs,
            `${sourcePrefix}-donor-fresh-egg-rates`,
            {
              treatmentType,
              population: "donor_eggs",
              embryoSource: "fresh_from_fresh_eggs",
              metric: outcomeMetric,
            },
          ),
          freshEmbryosFrozenEggs: clinicSingleRate(
            clinic.donorSuccessRates.freshEmbryosFrozenEggs,
            clinic.donorSuccessRateDenominators?.freshEmbryosFrozenEggs,
            `${sourcePrefix}-donor-frozen-egg-rates`,
            {
              treatmentType,
              population: "donor_eggs",
              embryoSource: "fresh_from_frozen_eggs",
              metric: outcomeMetric,
            },
          ),
          frozenEmbryos: clinicSingleRate(
            clinic.donorSuccessRates.frozenEmbryos,
            clinic.donorSuccessRateDenominators?.frozenEmbryos,
            `${sourcePrefix}-donor-frozen-embryo-rates`,
            {
              treatmentType: "frozen_embryo_transfer",
              population: "donor_eggs",
              embryoSource: "frozen_embryos",
              metric: outcomeMetric,
            },
          ),
          donatedEmbryos: clinicSingleRate(
            clinic.donorSuccessRates.donatedEmbryos,
            clinic.donorSuccessRateDenominators?.donatedEmbryos,
            `${sourcePrefix}-donated-embryo-rates`,
            {
              treatmentType: "frozen_embryo_transfer",
              population: "donated_embryos",
              embryoSource: "donated_embryos",
              metric: outcomeMetric,
            },
          ),
        }
      : undefined,
    cyclesPerYear:
      typeof clinic.nbCyclePerYear === "number" && clinic.nbCyclePerYear > 0
        ? clinicObserved(clinic.nbCyclePerYear, `${sourcePrefix}-volume`)
        : undefined,
    denominatorDescription: clinic.outcomeDenominatorDescription ?? undefined,
    reportYear: clinic.lastReportYear,
    reportingPopulation: clinicReportingPopulation(clinic, population),
    patientModifiers: {
      diagnoses: observed(input.diagnoses, "patient-diagnoses"),
      bmi: input.bmi ? observed(input.bmi, "patient-bmi") : undefined,
      smoking: observed(
        input.isSmoking ? "current" : input.isFormerSmoker ? "former" : "never",
        "patient-smoking",
      ),
      priorLiveBirth: observed(
        input.hasPriorLiveBirth,
        "patient-pregnancy-history",
      ),
      priorMiscarriage: observed(
        input.hasPriorMiscarriage,
        "patient-pregnancy-history",
      ),
      priorFailedCycles: observed(
        input.priorFailedIvfCycles,
        "patient-cycle-history",
      ),
    },
  };
}

function clinicOutcomeSourcePrefix(clinic: Clinic): string {
  const source = clinicOutcomeSourceSystem(clinic);
  return `clinic-${clinic.id || "unknown"}-${source}-${clinic.lastReportYear ?? "unknown"}`;
}

function clinicOutcomeSourceSystem(
  clinic: Clinic,
): "cdc-nass" | "sart" | "clinic" {
  const reportedTo = clinic.reportedTo?.trim().toLowerCase();
  if (reportedTo === "sart") return "sart";
  if (reportedTo === "cdc" || reportedTo === "nass") return "cdc-nass";
  return "clinic";
}

function clinicReportingPopulation(
  clinic: Clinic,
  population: ClinicTreatmentSuccessInput["population"],
): string | undefined {
  const sourceSystem = clinicOutcomeSourceSystem(clinic);
  if (sourceSystem === "cdc-nass") {
    return population === "own_eggs"
      ? "CDC/NASS Q019 no-filter: patients using own eggs; age-band percentage of transfers resulting in live-birth deliveries"
      : "CDC/NASS Q030 no-filter: patients using donor eggs/embryos; embryo-source percentage of transfers resulting in live-birth deliveries";
  }
  if (clinic.outcomeReportingPopulation) {
    return clinic.outcomeReportingPopulation;
  }
  if (sourceSystem === "sart") {
    return "SART clinic summary report; treatment population and endpoint must match the selected rate cell";
  }
  return undefined;
}

function clinicProjectionEvidence(clinic: Clinic) {
  const sourceSystem = clinicOutcomeSourceSystem(clinic);
  const baseSourceId =
    sourceSystem === "sart"
      ? "sart-national-summary-2024"
      : sourceSystem === "cdc-nass"
        ? "cdc-nass-2022-final-art-success-rates"
        : undefined;
  const baseSource = CLINIC_PROJECTION_EVIDENCE.find(
    (source) => source.id === baseSourceId,
  );
  const sourcePrefix = clinicOutcomeSourcePrefix(clinic);
  const rateSourceIds = [
    `${sourcePrefix}-own-egg-rates`,
    `${sourcePrefix}-first-cycle-rates`,
    `${sourcePrefix}-donor-fresh-egg-rates`,
    `${sourcePrefix}-donor-frozen-egg-rates`,
    `${sourcePrefix}-donor-frozen-embryo-rates`,
    `${sourcePrefix}-donated-embryo-rates`,
  ];
  const dynamicSources = rateSourceIds.map((sourceId) => ({
    id: sourceId,
    label: `${clinic.reportedTo ?? "Clinic"} reported treatment outcomes for ${clinic.name}`,
    role: "parameter_source" as const,
    citation: `${clinic.reportedTo ?? "Clinic"} outcome record ${clinic.id}`,
    url:
      clinic.outcomeSourceUrl ??
      (sourceSystem === "sart"
        ? "https://www.sartcorsonline.com/rptCSR_PublicMultYear.aspx?ClinicPKID=0"
        : sourceSystem === "cdc-nass"
          ? "https://data.cdc.gov/resource/cchw-gdwa.json"
          : undefined),
    population:
      sourceSystem === "cdc-nass"
        ? "CDC/NASS clinic-level outcome cell; treatment, population, embryo source, and age context are retained separately"
        : `${clinic.reportedTo ?? "Clinic"}-reported clinic-level treatment outcomes`,
    publishedAt: clinic.lastReportYear?.toString(),
    verificationStatus: "verified" as const,
  }));
  return [
    ...(baseSource ? [baseSource] : []),
    ...(sourceSystem === "cdc-nass"
      ? CLINIC_PROJECTION_EVIDENCE.filter(
          (source) => source.id === "cdc-nass-2022-services-and-profiles",
        )
      : []),
    ...dynamicSources,
    ...(sourceSystem === "cdc-nass"
      ? CLINIC_PROJECTION_EVIDENCE.filter(
          (source) => source.id === "sart-national-summary-2024",
        )
      : []),
  ];
}
